/**
 * outliner-conversion.ts - Convert between Organizr lists and Outlinr nodes
 * 
 * CHANGE HISTORY:
 * - 251130_1612 danielbeer: Created conversion utilities for Outlinr integration
 * 
 * These utilities enable seamless integration between Organizr's list structure
 * and Outlinr's hierarchical editing interface.
 */

import type { ListItem, OutlinerConversionOptions } from '../types';

// Temporary OutlinerNode type until we can import from @opcentral/outlinr
interface OutlinerNode {
  id: string;
  text: string;
  parentId: string | null;
  generation: number;
  expanded: boolean;
  children: string[];
}

/**
 * Convert Organizr list items to Outlinr nodes (hierarchical format)
 * 
 * @param items - List items to convert
 * @param parentId - Parent node ID (null for root)
 * @param generation - Depth level in hierarchy
 * @param options - Conversion options
 * @returns Array of OutlinerNode objects
 */
export function convertToOutlinerNodes(
  items: ListItem[],
  parentId: string | null = null,
  generation: number = 0,
  options: OutlinerConversionOptions = {}
): OutlinerNode[] {
  return items.map((item) => {
    const node: OutlinerNode & { listMetadata?: any } = {
      id: item.id,
      text: item.name,
      parentId,
      generation,
      expanded: true,
      children: item.children ? item.children.map(c => c.id) : []
    };
    
    // Store list-specific metadata if requested
    if (options.preserveMetadata !== false) {
      node.listMetadata = {
        value: item.value,
        color: item.color,
        icon: item.icon,
        pattern: item.pattern,
        order: item.order,
        ...item.metadata
      };
    }
    
    return node;
  });
}

/**
 * Flatten Organizr list items to Outlinr nodes (flat array format)
 * 
 * This is the format Outlinr expects - a flat array with parent/child relationships
 * 
 * @param items - List items to flatten
 * @param parentId - Parent node ID (null for root)
 * @param generation - Depth level in hierarchy
 * @param options - Conversion options
 * @returns Flattened array of OutlinerNode objects
 */
export function flattenNodes(
  items: ListItem[],
  parentId: string | null = null,
  generation: number = 0,
  options: OutlinerConversionOptions = {}
): OutlinerNode[] {
  const result: (OutlinerNode & { listMetadata?: any })[] = [];
  
  for (const item of items) {
    const node: OutlinerNode & { listMetadata?: any } = {
      id: item.id,
      text: item.name,
      parentId,
      generation,
      expanded: true,
      children: item.children ? item.children.map(c => c.id) : []
    };
    
    // Store list-specific metadata
    if (options.preserveMetadata !== false) {
      node.listMetadata = {
        value: item.value,
        color: item.color,
        icon: item.icon,
        pattern: item.pattern,
        order: item.order,
        ...item.metadata
      };
    }
    
    result.push(node);
    
    // Recursively flatten children
    if (item.children && item.children.length > 0) {
      result.push(...flattenNodes(item.children, item.id, generation + 1, options));
    }
  }
  
  return result;
}

/**
 * Convert Outlinr nodes back to Organizr list items
 * 
 * @param nodes - Outlinr nodes to convert
 * @param defaultColor - Default color for items without metadata
 * @param defaultPattern - Default pattern for items without metadata
 * @returns Array of ListItem objects
 */
export function convertFromOutlinerNodes(
  nodes: (OutlinerNode & { listMetadata?: any })[],
  defaultColor: string = '#6b7280',
  defaultPattern: string = 'solid'
): ListItem[] {
  // Build a map of nodes by ID
  const nodeMap = new Map<string, OutlinerNode & { listMetadata?: any }>();
  nodes.forEach(node => nodeMap.set(node.id, node));
  
  // Find root nodes (no parent or parent not in map)
  const rootNodes = nodes.filter(node => !node.parentId || !nodeMap.has(node.parentId));
  
  /**
   * Recursively build list item from node
   */
  const buildItem = (node: OutlinerNode & { listMetadata?: any }, index: number): ListItem => {
    const metadata = node.listMetadata || {};
    const childNodes = node.children
      ?.map(childId => nodeMap.get(childId))
      .filter(Boolean) as (OutlinerNode & { listMetadata?: any })[] || [];
    
    return {
      id: node.id,
      name: node.text,
      value: metadata.value || node.text.toLowerCase().replace(/\s+/g, '-'),
      color: metadata.color || defaultColor,
      icon: metadata.icon || null,
      pattern: metadata.pattern || defaultPattern,
      order: metadata.order ?? index,
      children: childNodes.length > 0 
        ? childNodes.map((child, i) => buildItem(child, i))
        : undefined,
      metadata: metadata
    };
  };
  
  return rootNodes.map((node, index) => buildItem(node, index));
}

/**
 * Validate that nodes can be converted to list items
 * 
 * @param nodes - Nodes to validate
 * @returns Validation result with any errors
 */
export function validateNodesForConversion(
  nodes: OutlinerNode[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check for duplicate IDs
  const ids = new Set<string>();
  for (const node of nodes) {
    if (ids.has(node.id)) {
      errors.push(`Duplicate node ID: ${node.id}`);
    }
    ids.add(node.id);
  }
  
  // Check for orphaned nodes (parent doesn't exist)
  const nodeIds = new Set(nodes.map(n => n.id));
  for (const node of nodes) {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      errors.push(`Orphaned node: ${node.id} references non-existent parent ${node.parentId}`);
    }
  }
  
  // Check for circular references
  const visited = new Set<string>();
  const checkCircular = (nodeId: string, path: Set<string>): boolean => {
    if (path.has(nodeId)) {
      errors.push(`Circular reference detected: ${Array.from(path).join(' -> ')} -> ${nodeId}`);
      return true;
    }
    
    if (visited.has(nodeId)) return false;
    
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return false;
    
    visited.add(nodeId);
    path.add(nodeId);
    
    if (node.parentId) {
      checkCircular(node.parentId, path);
    }
    
    path.delete(nodeId);
    return false;
  };
  
  for (const node of nodes) {
    checkCircular(node.id, new Set());
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}
